import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import analyticsService from '../../services/analyticsService';

interface ClientAnalyticsProps {
  type: 'growth' | 'detailed';
  dateRange: {
    from: string;
    to: string;
  };
}

const ClientAnalytics: React.FC<ClientAnalyticsProps> = ({ type, dateRange }) => {
  const [growthData, setGrowthData] = useState<Array<{ date: string; newClients: number }>>([]);
  const [statusData, setStatusData] = useState<Array<{ status: string; count: number; color?: string }>>([]);
  const [totalClients, setTotalClients] = useState<number>(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await analyticsService.getClientAnalytics(dateRange);
        if (!mounted) return;
        const growth = Object.entries(res.growthByDate || {})
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, count]) => ({ date, newClients: Number(count) }));
        const status = (res.statusDistribution || []).map((s: any) => ({
          status: s.status,
          count: s._count?.status || 0,
        }));
        setGrowthData(growth);
        setStatusData(status);
        setTotalClients(res.totalClients || 0);
      } catch (error) {
        // Handle error silently
      }
    };
    load();
    return () => { mounted = false; };
  }, [dateRange]);

  if (type === 'growth') {
    return (
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={growthData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey="newClients" 
              stroke="#3B82F6" 
              strokeWidth={2}
              name="New Clients"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }


  if (type === 'detailed') {
    return (
      <div className="space-y-8">
        {/* Client Growth Over Time */}
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Growth Over Time</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="newClients"
                  stackId="1"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.6}
                  name="New Clients"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Client Status Distribution */}
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Status Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="status" type="category" width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{totalClients.toLocaleString()}</div>
            <div className="text-sm text-blue-800">Total Clients</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{(statusData.find(s => s.status === 'ACTIVE')?.count || 0).toLocaleString()}</div>
            <div className="text-sm text-green-800">Active Clients</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ClientAnalytics;
